"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button, Card, Spin, Tag, Empty, Grid, Table, Row, Col } from "antd";
import type { TableColumnsType } from "antd";
import { LoadingOutlined, CodeOutlined, ClockCircleOutlined, DesktopOutlined, MobileOutlined, TabletOutlined, GlobalOutlined, ChromeOutlined, TableOutlined } from "@ant-design/icons";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { LineChart, PieChart, BarChart, MapChart, EffectScatterChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent, GeoComponent, VisualMapComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useLocale } from "next-intl";
import styles from "./StatisticDashboard.module.sass";
echarts.use([LineChart, PieChart, BarChart, MapChart, EffectScatterChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, GeoComponent, VisualMapComponent, CanvasRenderer]);
interface QueryResult {
  data: Record<string, unknown>[];
  sql: string;
  description: string;
  executionTimeMs: number;
  cachedAt: string;
  fromCache: boolean;
}
type QueryType = "dailyVisits" | "geoDistribution" | "languageDistribution" | "deviceDistribution" | "browserDistribution" | "osDistribution" | "screenDistribution";
const WORLD_MAP_NAME = "world";
const WORLD_MAP_GEOJSON_URL = "https://fastly.jsdelivr.net/npm/echarts@5/map/json/world.json";
let worldMapPromise: Promise<void> | null = null;
async function ensureWorldMapRegistered() {
  if (echarts.getMap(WORLD_MAP_NAME)) return;
  if (!worldMapPromise) {
    worldMapPromise = fetch(WORLD_MAP_GEOJSON_URL, {
      cache: "force-cache"
    }).then(res => {
      if (!res.ok) throw new Error("Failed to download world map");
      return res.json() as Promise<unknown>;
    }).then(geoJson => {
      echarts.registerMap(WORLD_MAP_NAME, geoJson as never);
    });
  }
  try {
    await worldMapPromise;
  } catch (error) {
    worldMapPromise = null;
    throw error;
  }
}
const intlLocaleByAppLocale: Record<string, string> = {
  "en-US": "en-US",
  "zh-Hans": "zh-CN",
  "zh-Hant": "zh-TW"
};

/**
 * Formats a timestamp with an explicit locale and time zone.
 *
 * `toLocaleString()` without arguments resolves from the host environment, so the
 * server-rendered markup and the first client render disagreed (e.g.
 * `2024/6/11 21:11:00` vs `6/11/2024, 9:11:00 PM`) and triggered a hydration
 * mismatch. Pinning both the locale and the zone makes SSR and CSR agree.
 */
function formatUpdatedAt(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(intlLocaleByAppLocale[locale] ?? "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Taipei"
  }).format(date);
}
function SqlPanel(t0) {
  const $ = _c(22);
  const {
    sql,
    executionTimeMs,
    cachedAt,
    fromCache,
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span><CodeOutlined aria-hidden={true} /> SQL Query</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ClockCircleOutlined aria-hidden={true} data-sentry-element="ClockCircleOutlined" data-sentry-source-file="StatisticDashboard.tsx" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== executionTimeMs) {
    t3 = <Tag color="blue" variant="filled" data-sentry-element="Tag" data-sentry-source-file="StatisticDashboard.tsx">{t2} {executionTimeMs} ms</Tag>;
    $[2] = executionTimeMs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = fromCache ? "green" : "gold";
  const t5 = fromCache ? "Cached" : "Refreshed";
  let t6;
  if ($[4] !== t4 || $[5] !== t5) {
    t6 = <Tag color={t4} variant="filled" data-sentry-element="Tag" data-sentry-source-file="StatisticDashboard.tsx">{t5}</Tag>;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t3 || $[8] !== t6) {
    t7 = <div className={styles.sqlMeta}>{t3}{t6}</div>;
    $[7] = t3;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== cachedAt || $[11] !== locale) {
    t8 = formatUpdatedAt(cachedAt, locale);
    $[10] = cachedAt;
    $[11] = locale;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <div className={styles.updatedAt}>Updated: {t8}</div>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      margin: 0,
      borderRadius: 8,
      fontSize: 12,
      lineHeight: 1.55
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== sql) {
    t11 = <SyntaxHighlighter language="sql" style={oneLight} customStyle={t10} wrapLongLines={true} data-sentry-element="SyntaxHighlighter" data-sentry-source-file="StatisticDashboard.tsx">{sql}</SyntaxHighlighter>;
    $[16] = sql;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t7 || $[20] !== t9) {
    t12 = <Card size="small" className={styles.sqlPanel} title={t1} data-sentry-element="Card" data-sentry-component="SqlPanel" data-sentry-source-file="StatisticDashboard.tsx">{t7}{t9}{t11}</Card>;
    $[18] = t11;
    $[19] = t7;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}
function formatTableValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
function formatDateWithWeekday(value: unknown, locale: string): React.ReactNode {
  const raw = String(value ?? "");
  const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return formatTableValue(value);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day) || Number.isNaN(date.getTime()) || date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return formatTableValue(value);
  }
  const formattedWeekday = new Intl.DateTimeFormat(intlLocaleByAppLocale[locale] ?? "en-US", {
    weekday: locale === "en-US" ? "short" : "long"
  }).format(date);
  const weekday = locale === "en-US" && !formattedWeekday.endsWith(".") ? `${formattedWeekday}.` : formattedWeekday;
  const dateText = `${year}-${month}-${day}`;
  return `${dateText} (${weekday})`;
}
function ResultTable(t0) {
  const $ = _c(18);
  const {
    data,
    queryType
  } = t0;
  const locale = useLocale();
  let t1;
  bb0: {
    if (queryType !== "dailyVisits") {
      t1 = data;
      break bb0;
    }
    let t2;
    if ($[0] !== data) {
      const grouped = new Map();
      for (const row of data) {
        const date = String(row.date ?? "").slice(0, 10);
        if (!date) {
          continue;
        }
        const device = String(row.device_type ?? "desktop").toLowerCase();
        const count = Number(row.count ?? 0);
        const safeCount = Number.isFinite(count) ? count : 0;
        if (!grouped.has(date)) {
          grouped.set(date, {
            date,
            "count(desktop)": 0,
            "count(tablet)": 0,
            "count(mobile)": 0
          });
        }
        const current = grouped.get(date);
        if (!current) {
          continue;
        }
        if (device === "tablet") {
          current["count(tablet)"] = current["count(tablet)"] + safeCount;
        } else {
          if (device === "mobile") {
            current["count(mobile)"] = current["count(mobile)"] + safeCount;
          } else {
            current["count(desktop)"] = current["count(desktop)"] + safeCount;
          }
        }
      }
      t2 = Array.from(grouped.values()).sort(_temp);
      $[0] = data;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  }
  const tableData = t1;
  let t2;
  bb1: {
    if (queryType === "dailyVisits") {
      t2 = "date";
      break bb1;
    }
    let t3;
    if ($[2] !== tableData[0]) {
      t3 = Object.keys(tableData[0] ?? {})[0] ?? "key";
      $[2] = tableData[0];
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = t3;
  }
  const rowKeyField = t2;
  let t3;
  bb2: {
    if (queryType === "dailyVisits") {
      let t4;
      if ($[4] !== locale) {
        t4 = {
          title: "date",
          dataIndex: "date",
          key: "date",
          ellipsis: true,
          render: value => formatDateWithWeekday(value, locale)
        };
        $[4] = locale;
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      let t5;
      let t6;
      let t7;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = {
          title: "desktop",
          dataIndex: "count(desktop)",
          key: "count(desktop)",
          ellipsis: true,
          render: _temp2
        };
        t6 = {
          title: "tablet",
          dataIndex: "count(tablet)",
          key: "count(tablet)",
          ellipsis: true,
          render: _temp3
        };
        t7 = {
          title: "mobile",
          dataIndex: "count(mobile)",
          key: "count(mobile)",
          ellipsis: true,
          render: _temp4
        };
        $[6] = t5;
        $[7] = t6;
        $[8] = t7;
      } else {
        t5 = $[6];
        t6 = $[7];
        t7 = $[8];
      }
      let t8;
      if ($[9] !== t4) {
        t8 = [t4, t5, t6, t7];
        $[9] = t4;
        $[10] = t8;
      } else {
        t8 = $[10];
      }
      t3 = t8;
      break bb2;
    }
    let t4;
    if ($[11] !== tableData[0]) {
      t4 = Object.keys(tableData[0] ?? {}).map(_temp6);
      $[11] = tableData[0];
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = t4;
  }
  const columns = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span><TableOutlined /> Query Result</span>;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== columns || $[15] !== rowKeyField || $[16] !== tableData) {
    t5 = <Card size="small" className={styles.tablePanel} title={t4} data-sentry-element="Card" data-sentry-component="ResultTable" data-sentry-source-file="StatisticDashboard.tsx">{tableData.length > 0 ? <Table size="small" columns={columns} dataSource={tableData} rowKey={rowKeyField} pagination={tableData.length > 7 ? {
        pageSize: 7,
        showSizeChanger: false
      } : false} scroll={{
        x: "max-content"
      }} /> : <Empty description="No data yet" />}</Card>;
    $[14] = columns;
    $[15] = rowKeyField;
    $[16] = tableData;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
function _temp6(key) {
  return {
    title: key,
    dataIndex: key,
    key,
    ellipsis: true,
    render: _temp5
  };
}
function _temp5(value_3) {
  return formatTableValue(value_3);
}
function _temp4(value_2) {
  return formatTableValue(value_2);
}
function _temp3(value_1) {
  return formatTableValue(value_1);
}
function _temp2(value_0) {
  return formatTableValue(value_0);
}
function _temp(a, b) {
  return a.date.localeCompare(b.date);
}
function ChartCard({
  title,
  icon,
  queryType,
  requiresWorldMap = false,
  renderChart
}: {
  title: string;
  icon: React.ReactNode;
  queryType: QueryType;
  requiresWorldMap?: boolean;
  renderChart: (data: QueryResult["data"], context: {
    worldMapReady: boolean;
    isMobile: boolean;
  }) => echarts.EChartsCoreOption;
}) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const locale = useLocale();
  const [state, setState] = useState<{
    status: "loading" | "error" | "ready";
    result: QueryResult | null;
  }>({
    status: "loading",
    result: null
  });
  const [retryCount, setRetryCount] = useState(0);
  const [worldMapReady, setWorldMapReady] = useState(!requiresWorldMap);
  const [worldMapLoadFailed, setWorldMapLoadFailed] = useState(false);
  const {
    status,
    result
  } = state;
  const loading = status === "loading";
  useEffect(() => {
    if (!requiresWorldMap) return;
    let active = true;
    ensureWorldMapRegistered().then(() => {
      if (active) setWorldMapReady(true);
    }).catch(() => {
      if (active) setWorldMapLoadFailed(true);
    });
    return () => {
      active = false;
    };
  }, [requiresWorldMap]);

  // Note: no synchronous `setState` here. The previous version called
  // `setLoading(true)` / `setError(false)` at the top of the effect body, which
  // triggered a cascading render (react-hooks/set-state-in-effect) and left the
  // card permanently stuck on "failed" with no way to retry.
  useEffect(() => {
    let active_0 = true;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(`/api/statistic?type=${queryType}`, {
          signal: controller.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: QueryResult = await res.json();
        if (active_0) setState({
          status: "ready",
          result: json
        });
      } catch {
        // Ignore aborts so unmounting or re-querying does not flash an error.
        if (active_0 && !controller.signal.aborted) {
          setState({
            status: "error",
            result: null
          });
        }
      }
    })();
    return () => {
      active_0 = false;
      controller.abort();
    };
  }, [queryType, retryCount]);
  const retry = useCallback(() => {
    setRetryCount(count => count + 1);
  }, []);
  const chartOption = useMemo(() => {
    if (!result) return null;
    return renderChart(result.data, {
      worldMapReady: worldMapReady && !worldMapLoadFailed,
      isMobile
    });
  }, [isMobile, renderChart, result, worldMapReady, worldMapLoadFailed]);
  const waitingMap = requiresWorldMap && !worldMapReady && !worldMapLoadFailed;
  return <Card title={<div className={styles.cardTitle}>
          <span>
            {icon} {title}
          </span>
          {result ? <span className={styles.cardUpdatedAt}>
              Updated: {formatUpdatedAt(result.cachedAt, locale)}
            </span> : null}
        </div>} className={styles.chartCard} data-sentry-element="Card" data-sentry-component="ChartCard" data-sentry-source-file="StatisticDashboard.tsx">
      {(loading || waitingMap) && <div className={styles.loading}>
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>}
      {status === "error" && <div className={styles.errorState}>
          <Empty description="Failed to load data" />
          <Button onClick={retry}>Retry</Button>
        </div>}
      {!loading && !waitingMap && status !== "error" && result && <div className={styles.cardContent}>
          <Row gutter={[isMobile ? 12 : 16, 16]} className={styles.contentRow} align="top">
            <Col xs={24} sm={24} md={14} lg={16} xl={16} className={styles.chartSection}>
              {result.data.length > 0 && chartOption ? <ReactEChartsCore echarts={echarts} option={chartOption} style={{
            height: isMobile ? 320 : 420
          }} notMerge /> : <Empty description="No data yet" />}
            </Col>
            <Col xs={24} sm={24} md={10} lg={8} xl={8} className={styles.tableSection}>
              <ResultTable data={result.data} queryType={queryType} />
            </Col>
          </Row>
          <div className={styles.sqlSection}>
            <SqlPanel sql={result.sql} executionTimeMs={result.executionTimeMs} cachedAt={result.cachedAt} fromCache={result.fromCache} locale={locale} />
            {worldMapLoadFailed ? <Tag color="warning" className={styles.mapWarning}>
                World map failed to load, fallback chart is shown.
              </Tag> : null}
          </div>
        </div>}
    </Card>;
}
function buildDailyVisitsOption(data: Record<string, unknown>[]): echarts.EChartsCoreOption {
  const dateSet = new Set<string>();
  const deviceTypes = ["desktop", "tablet", "mobile"];
  const deviceMap: Record<string, Record<string, number>> = {};
  for (const row of data) {
    const date = String(row.date ?? "").slice(0, 10);
    const device = String(row.device_type ?? "desktop");
    const count = Number(row.count ?? 0);
    if (!date) continue;
    dateSet.add(date);
    if (!deviceMap[device]) deviceMap[device] = {};
    deviceMap[device][date] = (deviceMap[device][date] || 0) + count;
  }
  const dates = Array.from(dateSet).sort();
  const colorMap: Record<string, string> = {
    desktop: "#5B8FF9",
    tablet: "#5AD8A6",
    mobile: "#F6BD16"
  };
  const nameMap: Record<string, string> = {
    desktop: "Desktop",
    tablet: "Tablet",
    mobile: "Mobile"
  };
  return {
    tooltip: {
      trigger: "axis"
    },
    legend: {
      data: deviceTypes.map(d => nameMap[d]),
      top: 8,
      left: "center"
    },
    grid: {
      left: 12,
      right: 16,
      top: 72,
      bottom: 20,
      containLabel: true
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: dates
    },
    yAxis: {
      type: "value"
    },
    series: deviceTypes.map(device => ({
      name: nameMap[device],
      type: "line",
      stack: "visits",
      areaStyle: {},
      emphasis: {
        focus: "series"
      },
      color: colorMap[device],
      data: dates.map(d => deviceMap[device]?.[d] ?? 0)
    }))
  };
}
function buildPieOption(data: Record<string, unknown>[], nameKey: string, isMobile: boolean): echarts.EChartsCoreOption {
  const values = data.map(row => ({
    name: String(row[nameKey] ?? "Unknown"),
    value: Number(row.count ?? 0)
  }));
  return {
    tooltip: {
      trigger: "item",
      formatter: "{b}: {c} ({d}%)"
    },
    legend: {
      type: "scroll",
      orient: isMobile ? "horizontal" : "vertical",
      ...(isMobile ? {
        left: "center",
        right: "auto",
        bottom: 0
      } : {
        right: 8,
        top: 16,
        bottom: 16
      })
    },
    grid: isMobile ? {
      left: 8,
      right: 8,
      top: 8,
      bottom: 8,
      containLabel: true
    } : undefined,
    series: [{
      type: "pie",
      center: isMobile ? ["50%", "38%"] : ["34%", "50%"],
      radius: isMobile ? ["38%", "56%"] : ["40%", "68%"],
      avoidLabelOverlap: true,
      itemStyle: {
        borderRadius: 6,
        borderColor: "#fff",
        borderWidth: 2
      },
      label: {
        show: false
      },
      labelLine: {
        show: false
      },
      emphasis: {
        label: {
          show: true,
          formatter: "{b}\n{d}%",
          fontWeight: "bold"
        }
      },
      data: values
    }]
  };
}
function buildBarOption(data: Record<string, unknown>[], nameKey: string, isMobile: boolean): echarts.EChartsCoreOption {
  return {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow"
      }
    },
    grid: {
      left: 12,
      right: 16,
      bottom: isMobile ? 82 : 64,
      containLabel: true
    },
    xAxis: {
      type: "category",
      data: data.map(row => String(row[nameKey] ?? "Unknown")),
      axisLabel: {
        rotate: isMobile ? 36 : 24,
        interval: 0,
        width: isMobile ? 70 : 92,
        overflow: "truncate"
      }
    },
    yAxis: {
      type: "value"
    },
    series: [{
      type: "bar",
      data: data.map(row => Number(row.count ?? 0)),
      itemStyle: {
        borderRadius: [4, 4, 0, 0]
      },
      colorBy: "data"
    }]
  };
}
function buildGeoMapOption(data: Record<string, unknown>[], context: {
  worldMapReady: boolean;
  isMobile: boolean;
}): echarts.EChartsCoreOption {
  if (!context.worldMapReady) {
    return buildBarOption(data, "country", context.isMobile);
  }
  const countryData = new Map<string, number>();
  const points: Array<{
    name: string;
    value: [number, number, number];
  }> = [];
  for (const row of data) {
    const country = String(row.country ?? "Unknown");
    const count = Number(row.count ?? 0);
    const latitude = Number(row.latitude);
    const longitude = Number(row.longitude);
    countryData.set(country, (countryData.get(country) || 0) + count);
    if (Number.isFinite(latitude) && Number.isFinite(longitude) && country !== "Unknown") {
      points.push({
        name: country,
        value: [longitude, latitude, count]
      });
    }
  }
  const mapData = Array.from(countryData.entries()).map(([name, value]) => ({
    name,
    value
  }));
  const maxValue = Math.max(1, ...mapData.map(item => Number(item.value || 0)));
  return {
    tooltip: {
      trigger: "item",
      formatter: (params: {
        name?: string;
        value?: unknown;
      }) => {
        if (Array.isArray(params.value)) {
          const count = Number(params.value[2] ?? 0);
          return `${params.name ?? "Unknown"}: ${count}`;
        }
        return `${params.name ?? "Unknown"}: ${Number(params.value ?? 0)}`;
      }
    },
    visualMap: {
      min: 0,
      max: maxValue,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 8,
      inRange: {
        color: ["#dbeafe", "#93c5fd", "#3b82f6", "#1d4ed8"]
      },
      text: ["High", "Low"]
    },
    geo: {
      map: WORLD_MAP_NAME,
      roam: true,
      scaleLimit: {
        min: 1,
        max: 8
      },
      itemStyle: {
        areaColor: "#f8fafc",
        borderColor: "#94a3b8"
      },
      emphasis: {
        itemStyle: {
          areaColor: "#c7d2fe"
        }
      }
    },
    series: [{
      name: "Visits",
      type: "map",
      geoIndex: 0,
      data: mapData
    }, {
      name: "Geo Points",
      type: "effectScatter",
      coordinateSystem: "geo",
      data: points.sort((a, b) => b.value[2] - a.value[2]).slice(0, 120),
      symbolSize: (value: unknown) => {
        const count = Array.isArray(value) ? Number(value[2] ?? 0) : 0;
        return Math.max(6, Math.sqrt(Math.max(count, 1)) * 1.8);
      },
      rippleEffect: {
        scale: 3
      },
      itemStyle: {
        color: "#f97316",
        shadowBlur: 10,
        shadowColor: "rgba(249, 115, 22, 0.4)"
      }
    }]
  };
}
export function StatisticDashboard() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ChartCard title="Daily Visits" icon={<DesktopOutlined />} queryType="dailyVisits" renderChart={_temp7} data-sentry-element="ChartCard" data-sentry-source-file="StatisticDashboard.tsx" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChartCard title="Language Distribution" icon={<GlobalOutlined />} queryType="languageDistribution" renderChart={_temp8} data-sentry-element="ChartCard" data-sentry-source-file="StatisticDashboard.tsx" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChartCard title="Device Type Distribution" icon={<MobileOutlined />} queryType="deviceDistribution" renderChart={_temp9} data-sentry-element="ChartCard" data-sentry-source-file="StatisticDashboard.tsx" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChartCard title="Browser Distribution" icon={<ChromeOutlined />} queryType="browserDistribution" renderChart={_temp10} data-sentry-element="ChartCard" data-sentry-source-file="StatisticDashboard.tsx" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChartCard title="Operating System Distribution" icon={<TabletOutlined />} queryType="osDistribution" renderChart={_temp11} data-sentry-element="ChartCard" data-sentry-source-file="StatisticDashboard.tsx" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={styles.dashboard} data-sentry-component="StatisticDashboard" data-sentry-source-file="StatisticDashboard.tsx">{t0}{t1}{t2}{t3}{t4}<ChartCard title="Screen Resolution Distribution" icon={<DesktopOutlined />} queryType="screenDistribution" renderChart={_temp12} data-sentry-element="ChartCard" data-sentry-source-file="StatisticDashboard.tsx" /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function _temp12(data_4, context_3) {
  return buildBarOption(data_4, "resolution", context_3.isMobile);
}
function _temp11(data_3, context_2) {
  return buildBarOption(data_3, "os_name", context_2.isMobile);
}
function _temp10(data_2, context_1) {
  return buildBarOption(data_2, "browser_name", context_1.isMobile);
}
function _temp9(data_1, context_0) {
  return buildPieOption(data_1, "device_type", context_0.isMobile);
}
function _temp8(data_0, context) {
  return buildPieOption(data_0, "language", context.isMobile);
}
function _temp7(data) {
  return buildDailyVisitsOption(data);
}